import { PlanPrivacy, PlanType } from "common/app-plans";
import { IconImage } from "common/components/SerializedApp";
import { getNamedIcon } from "common/named-icons";
import { useBuilderTheme } from "hooks/use-builder-theme";
import { styled } from "sharedUI/styles";
import { Icon } from "common/render/icon";
import { GlideFC } from "lib/glide-fc";

interface AppPlanIconContainerProps {
    size: number;
    lockInset: number;
    showGradient: boolean;
    lockSize: number;
    gradientBaseColor?: string;
    gradientSecondColor?: string;
    gradientThirdColor?: string;
}

const AppPlanIconContainer = styled.div<AppPlanIconContainerProps>`
    position: relative;
    margin: ${p => (p.showGradient ? 24 : 0)}px;

    .gradient {
        border: 1.5px solid #ffffff;
        box-shadow: 0px 0px 0px 4px ${p => p.gradientBaseColor}, 0px 0px 0px 12px ${p => p.gradientSecondColor},
            0px 0px 0px 24px ${p => p.gradientThirdColor};
        border-radius: 15px;
    }

    .lock-icon {
        position: absolute;
        top: -${p => p.lockInset}px;
        left: -${p => p.lockInset}px;
        z-index: 100;
        display: flex;
        justify-content: center;
        align-items: center;
        height: ${p => p.lockSize}px;
        width: ${p => p.lockSize}px;
        background-color: ${p => p.theme.upgradeModal.lockColor};
        border-radius: ${p => p.lockSize}px;
    }
`;

interface Props {
    size: number | undefined;
    appAccentColor: string;
    appIcon: IconImage;
    shadow: boolean;
    plan: PlanType;
    privacy: PlanPrivacy;
}

const AppPlanIcon: GlideFC<Props> = p => {
    const { appAccentColor, appIcon, size, shadow, plan, privacy } = p;
    const LockIcon = getNamedIcon("lock");
    const theme = useBuilderTheme();

    const sizeOrDefault = size ?? 48;
    const lockInset = shadow ? 8 : 4;
    const lockSize = shadow ? 20 : 16;

    const isPublicPro = plan === PlanType.Pro && privacy === PlanPrivacy.Public;
    const isPrivatePro = plan === PlanType.Pro && privacy === PlanPrivacy.Private;

    let gradientBaseColor = theme.upgradeModal.basic.base;
    let gradientSecondColor = theme.upgradeModal.basic.gradientLighter;
    let gradientThirdColor = theme.upgradeModal.basic.gradientLightest;
    if (isPublicPro) {
        gradientBaseColor = theme.upgradeModal.pro.base;
        gradientSecondColor = theme.upgradeModal.pro.gradientLighter;
        gradientThirdColor = theme.upgradeModal.pro.gradientLightest;
    } else if (isPrivatePro) {
        gradientBaseColor = theme.upgradeModal.privatePro.base;
        gradientSecondColor = theme.upgradeModal.privatePro.gradientLighter;
        gradientThirdColor = theme.upgradeModal.privatePro.gradientLightest;
    }

    const baseContent = (
        <>
            {isPrivatePro && (
                <div className="lock-icon">
                    <LockIcon fgColor={theme.white} size={12} />
                </div>
            )}
            <Icon
                rounding={0}
                color={appAccentColor}
                image={appIcon}
                size={sizeOrDefault}
                clipCorners={true}
                loading={false}
            />
        </>
    );

    const content = shadow ? <div className="gradient">{baseContent}</div> : baseContent;

    return (
        <AppPlanIconContainer
            size={sizeOrDefault}
            lockInset={lockInset}
            lockSize={lockSize}
            showGradient={shadow}
            gradientBaseColor={gradientBaseColor}
            gradientSecondColor={gradientSecondColor}
            gradientThirdColor={gradientThirdColor}>
            {content}
        </AppPlanIconContainer>
    );
};

export default AppPlanIcon;
